import {
  AssetChangeColumns,
  AssetProps,
  PortfolioTableColumn,
  TableColumn
} from '@unistocks/features/portfolios/components/portfolio-table/types';
import { DateConstant, SectionAssetDto } from '@unistocks/proxy/axios-client';
import { getComparePrice, getTotalAssetQuantity } from '@unistocks/shared/utils/asset-utils';
import { isDefined, isNumber, titleCase } from '@unistocks/shared/utils/util';

/**
 * Returns true if the column should be visible.
 */
export const validateSectionProperty = (
  assets: Array<SectionAssetDto>,
  property: TableColumn
): boolean => {
  let assetProp: AssetProps,
    alwaysValid = false,
    key: AssetChangeColumns;

  switch (property) {
    case 'currentPriceChange':
      assetProp = 'previousPrice';
      break;

    case 'averageAcquisitionPrice':
      assetProp = 'calculatedAverageAcquisitionPrice';
      break;

    case 'averageAcquisitionPriceAssetCurrency':
      assetProp = 'calculatedAverageAcquisitionPriceAssetCurrency';
      break;

    case 'name':
    case 'calculatedValue':
    case 'calculatedValueShare':
      alwaysValid = true;
      break;

    case 'OneWeek':
    case 'OneMonth':
    case 'ThreeMonths':
    case 'SixMonths':
    case 'ThisYear':
    case 'OneYear':
    case 'ThreeYears':
    case 'FiveYears':
      assetProp = 'historicalPrices';
      key = property;
      break;

    case 'settings':
      alwaysValid = true;
      break;

    default:
      assetProp = property;
  }

  return alwaysValid || assets?.some(asset => validateAssetProperty(asset, assetProp, key));
};

/**
 * Returns true if the asset has the property asked for
 */
export const validateAssetProperty = (
  asset: SectionAssetDto,
  property: AssetProps,
  key?: AssetChangeColumns
): boolean => {
  switch (property) {
    case 'quantity':
      const totalQuantity = getTotalAssetQuantity(asset);
      return isNumber(totalQuantity) && totalQuantity > 0;

    case 'previousPrice':
      return isNumber(getComparePrice(asset, DateConstant.OneDay));

    case 'historicalPrices':
      if (!key) return false;
      return isNumber(getComparePrice(asset, DateConstant[key]));

    default:
      return isDefined(asset[property]);
  }
};

export const tableColumnKeyExtractor = ({ prop }: PortfolioTableColumn) => prop;

export const getColumnSize = (column: TableColumn): number => {
  switch (column) {
    case 'name':
      return 3;
    case 'currentPrice':
      return 3;
    case 'currentPriceChange':
      return 3;
    case 'totalChange':
      return 4;
    case 'calculatedValue':
      return 4;
    case 'calculatedValueShare':
      return 3;
    case 'averageAcquisitionPrice':
    case 'averageAcquisitionPriceAssetCurrency':
      return 4;

    case 'OneWeek':
    case 'OneMonth':
    case 'ThreeMonths':
    case 'SixMonths':
    case 'ThisYear':
    case 'OneYear':
    case 'ThreeYears':
    case 'FiveYears':
      return 3;

    case 'settings':
      return 0;

    default:
      return 4;
  }
};

export const getGenericColumn = (column: TableColumn): PortfolioTableColumn => ({
  prop: column,
  sortProp: titleCase(column),
  displayName: '',
  size: getColumnSize(column)
});
